/**
 * Authentication Context
 * Manages user authentication state and provides auth functions
 */

import type { ReactNode } from 'react';
import { createContext, useState, useEffect, useCallback } from 'react';
import type { User, Credentials, RegisterData, AuthResponse } from '@/types/auth';
import * as authApi from '@/api/auth';
import { 
  getAuthToken, 
  setAuthToken, 
  removeAuthToken,
  getUser,
  setUser as saveUser,
  removeUser,
  isTokenExpired
} from '@/utils/auth';

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: Credentials) => Promise<AuthResponse>;
  register: (data: RegisterData) => Promise<AuthResponse>;
  logout: () => void;
  forgotPassword: (email: string) => Promise<AuthResponse>;
  resetPassword: (email: string, code: string, newPassword: string) => Promise<AuthResponse>;
  updateUser: (updatedUser: User) => void;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        const token = getAuthToken();
        
        if (!token) {
          setIsLoading(false);
          return;
        }

        // Check if token is expired
        if (isTokenExpired(token)) {
          if (import.meta.env.DEV) {
            console.log('Token expired, clearing auth data');
          }
          removeAuthToken();
          removeUser();
          setIsLoading(false);
          return;
        }

        // Restore user from localStorage
        const savedUser = getUser();
        if (savedUser) {
          if (import.meta.env.DEV) {
            console.log('Restoring user session from localStorage');
          }
          setUser(savedUser as User);
        } else {
          if (import.meta.env.DEV) {
            console.log('Token exists but no user data found, clearing token');
          }
          removeAuthToken();
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error('Session check error:', error);
        removeAuthToken();
        removeUser();
        setIsLoading(false);
      }
    };

    checkSession();
  }, []);

  const login = useCallback(async (credentials: Credentials): Promise<AuthResponse> => {
    setIsLoading(true);
    
    try {
      const response = await authApi.login(credentials);
      
      if (response.success && response.user && response.token) {
        setUser(response.user);
        setAuthToken(response.token);
        saveUser(response.user); // Save user to localStorage
      }
      
      setIsLoading(false);
      return response;
    } catch (error) {
      setIsLoading(false);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Login failed',
      };
    }
  }, []);

  const register = useCallback(async (data: RegisterData): Promise<AuthResponse> => {
    setIsLoading(true);
    
    try {
      const response = await authApi.register(data);
      setIsLoading(false);
      return response;
    } catch (error) {
      setIsLoading(false);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Registration failed',
      };
    }
  }, []);

  const logout = useCallback(() => {
    authApi.logout();
    removeAuthToken();
    removeUser(); // Remove user from localStorage
    setUser(null);
  }, []);

  const forgotPassword = useCallback(async (email: string): Promise<AuthResponse> => {
    return authApi.forgotPassword(email);
  }, []);

  const resetPassword = useCallback(
    async (email: string, code: string, newPassword: string): Promise<AuthResponse> => {
      return authApi.resetPassword(email, code, newPassword);
    },
    []
  );

  const updateUser = useCallback((updatedUser: User) => {
    setUser(updatedUser);
    saveUser(updatedUser); // Save updated user to localStorage
  }, []);

  const value: AuthContextValue = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
